import { supabase } from "./churchSupabaseClient";
import { fetchOrganizationAccess } from "./churchOrganizationAccessService";

export function getChurchStreamAccount(user) {
  return (
    user?.churchOrgSession?.account ||
    user?.account ||
    user?.organization ||
    null
  );
}

export async function getChurchStreamAccountId(user, accountIdOverride = "") {
  if (accountIdOverride) return accountIdOverride;
  const directAccountId = getChurchStreamAccount(user)?.id || "";
  if (directAccountId) return directAccountId;
  if (!user?.id) return "";

  const access = await fetchOrganizationAccess(user.id, "church").catch(() => null);
  return access?.account?.id || "";
}

export async function getChurchStreamAuth(user, accountIdOverride = "") {
  return {
    accountId: await getChurchStreamAccountId(user, accountIdOverride),
    sessionToken: user?.churchOrgSession?.sessionToken || "",
    isOrgUser: Boolean(user?.churchOrgSession?.sessionToken),
  };
}

async function getSupabaseAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || "";
}

async function youtubeApiRequest({ method = "POST", action = "", user, accountId = "", body = {}, query = "" }) {
  const streamAuth = await getChurchStreamAuth(user, accountId);
  const accessToken = streamAuth.isOrgUser ? "" : await getSupabaseAccessToken();
  const headers = {
    "Content-Type": "application/json",
  };

  if (streamAuth.sessionToken) {
    headers["x-oikos-church-session"] = streamAuth.sessionToken;
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(`/api/church-youtube${query}`, {
    method,
    headers,
    body: method === "GET" ? undefined : JSON.stringify({
      action,
      churchId: streamAuth.accountId,
      sessionToken: streamAuth.sessionToken,
      ...body,
    }),
  });
  const payload = await response.json().catch(() => ({}));

  if (!response.ok || payload.error) {
    throw new Error(payload.error || "YouTube request failed.");
  }

  return payload;
}

export async function loadChurchStreamWorkspace(user, accountIdOverride = "") {
  const { accountId } = await getChurchStreamAuth(user, accountIdOverride);
  if (!accountId) {
    return { connection: null, events: [], relay: { configured: false } };
  }

  return youtubeApiRequest({
    method: "GET",
    user,
    accountId,
    query: `?churchId=${encodeURIComponent(accountId)}`,
  });
}

export async function startChurchYouTubeOAuth(user, accountId = "") {
  const payload = await youtubeApiRequest({ action: "start-oauth", user, accountId });
  if (!payload.authUrl) throw new Error("YouTube OAuth URL was not returned.");
  window.location.assign(payload.authUrl);
}

export async function createChurchYouTubeBroadcast(user, draft, accountId = "") {
  return youtubeApiRequest({
    action: "create-broadcast",
    user,
    accountId,
    body: {
      title: draft.title,
      description: draft.description,
      scheduledStartTime: draft.scheduledStartTime
        ? new Date(draft.scheduledStartTime).toISOString()
        : "",
      privacyStatus: draft.privacyStatus,
    },
  });
}

export async function saveChurchStreamDestination(user, values = {}, accountId = "") {
  return youtubeApiRequest({
    action: "save-destination",
    user,
    accountId,
    body: {
      rtmpUrl: values.rtmpUrl,
      streamKey: values.streamKey,
    },
  });
}

export async function clearChurchStreamDestination(user, accountId = "") {
  return youtubeApiRequest({
    action: "clear-destination",
    user,
    accountId,
  });
}

export async function transitionChurchYouTubeBroadcast(user, eventId, transition, accountId = "") {
  return youtubeApiRequest({
    action: "transition",
    user,
    accountId,
    body: { eventId, transition },
  });
}
